import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
// import { InwardRequestExport } from "./InwardRequestExport";
import posService from "../../services/pos/pos.service";
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";

interface Props {
  posId: number;
  onSendinwardRequest: (request: any) => void;
  onRejectinwardRequest: (request: any) => void;
  setLoading: (loading: boolean) => void;
}

export const InwardRawMaterialRequest: React.FC<Props> = ({
  posId,
  onSendinwardRequest,
  onRejectinwardRequest,
  setLoading,
}) => {
  const toast = useRef<Toast>(null);
  const [requests, setRequests] = useState<any[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [documentDetails, setDocumentDetails] = useState("");
  const [note, setNote] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await RawMaterialService.GetRawMaterialInwardRequests(posId);
        setRequests(response || []);
        setFilteredRequests(response || []);
      } catch (error) {
        console.error("Error fetching Inward requests", error);
        setRequests([]);
        setFilteredRequests([]);
      }
    };
    fetchRequests();
  }, [posId]);

  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    const lower = value.toLowerCase();
    setFilteredRequests(
      requests.filter(
        (req) =>
          req.rawMaterialName?.toLowerCase().includes(lower) ||
          req.requestToPosCode?.toLowerCase().includes(lower)
      )
    );
  };

  const handleAcceptSubmit = () => {
    if (!documentDetails.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Document Details are required.",
      });
      return;
    }
    const updated = { ...selectedRequest, documentDetails, note };
    onSendinwardRequest(updated);
    const removeId = selectedRequest.id;
setRequests(prev => prev.filter(r => r.id !== removeId));
setFilteredRequests(prev => prev.filter(r => r.id !== removeId));
    setShowAcceptDialog(false);
  };

  const handleRejectSubmit = () => {
    if (!note.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Note is required for rejection",
      });
      return;
    }
    const rejected = { ...selectedRequest, note };
    onRejectinwardRequest(rejected);
    const removeId = selectedRequest.id;
setRequests(prev => prev.filter(r => r.id !== removeId));
setFilteredRequests(prev => prev.filter(r => r.id !== removeId));
    setShowRejectDialog(false);
  };

  const formatDate = (dateStr: string) => {
    const dt = new Date(dateStr);
    return `${dt.getDate().toString().padStart(2, "0")}-${(dt.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${dt.getFullYear()}`;
  };

  const actions = (rowData: any) => (
    <div className="flex gap-2">
      <Button
        label="Accept"
        className="px-1 py-2 text-sm"
        icon="pi pi-send"
        onClick={() => {
          setSelectedRequest(rowData);
          setDocumentDetails("");
          setNote("");
          setShowAcceptDialog(true);
        }}
      />
      <Button
        label="Reject"
        className="px-1 py-2 text-sm"
        icon="pi pi-times"
        severity="danger"
        disabled
        onClick={() => {
          setSelectedRequest(rowData);
          setNote("");
          setShowRejectDialog(true);
        }}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <div className="flex justify-content-between align-items-center mb-3">
        <div className="p-input-icon-left w-full md:w-4">
          <i className="pi pi-search" />
          <InputText
            value={searchKeyword}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search Requests"
            className="w-full"
          />
        </div>
        {/* <Button
          icon="pi pi-file-excel"
          className="ml-3"
          text
          raised
          // onClick={() => InwardRequestExport(filteredRequests)}
        >
          Export Inward Requests
        </Button> */}
      </div>

      <DataTable
        value={filteredRequests}
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50, 100]}
         emptyMessage="No Inward requests found"
        className="p-datatable-sm"
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
      >
        <Column
          field="requestedDate"
          header="Sent Date"
          body={(row) => formatDate(row.respondedDate || row.requestedDate)}
          sortable
          style={{ width: "15%" }}
        />
        <Column field="rawMaterialName" header="Raw Material" style={{ width: "15%" }} sortable />
        <Column field="quantity" header="Quantity" style={{ width: "15%" }} sortable />
        <Column field="vendorName" header="Vendor" style={{ width: "15%" }} sortable />
        <Column field="invoiceNumber" header="Invoice Number" sortable />
        <Column field="requestToPosCode" header="Sent By" sortable />
        <Column header="Actions" body={actions} />
      </DataTable>

      {/* Accept Dialog */}
      <Dialog
        header="Accept Inward Request"
        visible={showAcceptDialog}
        onHide={() => setShowAcceptDialog(false)}
        style={{ width: "30vw" }}
        modal
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="doc">Document Details *</label>
            <InputText
              id="doc"
              value={documentDetails}
              onChange={(e) => setDocumentDetails(e.target.value)}
              placeholder="Enter document details"
              required
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>
          <div className="field mt-3">
            <label htmlFor="note">Note (optional)</label>
            <InputText
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter note if any"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
              maxLength={255}
            />
          </div>
          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={() => setShowAcceptDialog(false)}
            />
            <Button label="Submit" icon="pi pi-check" onClick={handleAcceptSubmit} />
          </div>
        </div>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        header="Reject Inward Request"
        visible={showRejectDialog}
        onHide={() => setShowRejectDialog(false)}
        style={{ width: "30vw" }}
        modal
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="rejectNote">Note *</label>
            <InputText
              id="rejectNote"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter reason for rejection"
              required
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
              maxLength={255}
            />
          </div>
          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={() => setShowRejectDialog(false)}
            />
            <Button label="Reject" icon="pi pi-check" severity="danger" onClick={handleRejectSubmit} />
          </div>
        </div>
      </Dialog>
    </>
  );
};
